import React, { memo } from "react";
import { useTutorialPage } from "./index.hooks";
import { AppButton, PageWrapper } from "components";
import { Box, Container, Grid, Stack, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { ReactComponent as RightArrow } from "assets/ui-icons/right-arrow.svg";
import { ReactComponent as Arrow } from "assets/ui-icons/arrow.svg";
import rotateTileGif from "assets/rotate-tile.gif";
import torinoGif from "assets/torino.gif";
import frecciaGif from "assets/freccia.gif";
import freezeGif from "assets/freeze.gif";

const Slide = styled(Box)`
  width: 100%;
  height: 100%;
  position: absolute;
  transition: all 0.33s ease-in-out;
  padding: 0 50px;
`;

const SlideContainer = styled(Box)`
  width: 100%;
  overflow: hidden;
  position: relative;
  height: calc(100vh - 250px);

  ${(props) => props.theme.breakpoints.up("md")} {
    height: calc(100vh - 250px);
  }
`;

const StyledDot = styled(Box)`
  width: 8px;
  height: 8px;
  overflow: hidden;
  border-radius: 4px;
  background: white;
  opacity: 0.5;
  cursor: pointer;

  :hover {
    background: rgba(255, 255, 255, 0.8);
  }

  ${(props) => props.theme.breakpoints.up("md")} {
    width: 14px;
    height: 14px;
    border-radius: 7px;
  }
`;

const SliderIcon = styled(Box)`
  position: absolute;
  top: calc(50% - 25px);
  margin: 0 10px;
  width: 35px;
  height: auto;
  cursor: pointer;
`;

const StyledTypography = styled(Box)`
  font-size: 18px;
  text-transform: none;
  font-weight: 400;
  line-height: 20px;
  flex: 1;
  max-width: 600px;

  strong {
    font-weight: bold;
  }

  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 35px;
    line-height: 40px;
  }
`;

const Gif = styled("img")`
  flex: 1;
  width: 100%;
  height: auto;
  object-fit: contain;
  max-width: 500px;

  ${(props) => props.theme.breakpoints.up("md")} {
    flex: initial;
  }
`;

const Spacer = styled("div")`
  flex: 1;

  ${(props) => props.theme.breakpoints.up("md")} {
    flex: 0;
  }
`;

const Dot = ({
  active,
  ...props
}: {
  active: boolean;
  onClick: () => void;
}) => {
  return (
    <StyledDot
      sx={{
        opacity: active ? 1 : 0.5,
      }}
      {...props}
    />
  );
};

type TutorialPageProps = {};

export const TutorialPage = memo(({}: TutorialPageProps) => {
  const {
    goToNextStep,
    goToPrevStep,
    step,
    goToStep,
    isMobile,
    isTallEnough,
  } = useTutorialPage();

  return (
    <PageWrapper>
      <Container maxWidth="xl" sx={{ position: "relative" }}>
        <Container maxWidth="lg">
          <SlideContainer>
            <Slide
              sx={{
                left: `${-step * 100}%`,
                padding: {
                  xs: "0 25px",
                  md: "0 50px",
                },
              }}
            >
              <Stack
                height="100%"
                direction={isMobile ? "column" : "row"}
                alignItems="center"
                spacing={4}
              >
                <StyledTypography>
                  <strong>
                    Il tuo obiettivo? Risolvi il puzzle e svela l’immagine
                    dell’opera!
                  </strong>
                  <br />
                  Clicca su una tessera e tieni premuto per ruotarla nel verso
                  che desideri. Ripeti questo passaggio per tutte le tessere
                  orientandole in modo che compongano la fotografia corretta.
                </StyledTypography>
                <Gif
                  alt=""
                  src={rotateTileGif}
                  sx={{
                    mt: {
                      xs: "0!important",
                      md: "unset",
                    },
                  }}
                />
                <Spacer />
              </Stack>
            </Slide>
            <Slide
              sx={{
                left: `${(1 - step) * 100}%`,
                padding: {
                  xs: "0 25px",
                  md: "0 50px",
                },
              }}
            >
              <Stack
                height="100%"
                direction={isMobile ? "column" : "row"}
                alignItems="center"
                spacing={4}
              >
                <StyledTypography>
                  Una volta <strong>ricostruita l’immagine</strong>, avrai vinto
                  la sfida e potrai <strong>accedere</strong> a approfondimenti,{" "}
                  <strong>news e curiosità</strong> esclusivi!
                </StyledTypography>
                <Gif
                  alt=""
                  src={torinoGif}
                  sx={{ width: { xs: "70%", md: "100%" } }}
                />
                <Spacer />
              </Stack>
            </Slide>
            <Slide
              sx={{
                left: `${(2 - step) * 100}%`,
                padding: {
                  xs: "0 25px",
                  md: "0 50px",
                },
              }}
            >
              <Stack
                height="100%"
                direction={isMobile ? "column" : "row"}
                alignItems="center"
                spacing={4}
              >
                <Box>
                  <StyledTypography>
                    Per ogni livello hai{" "}
                    <strong>due aiuti a disposizione</strong>:
                  </StyledTypography>
                  <AppButton
                    sx={{
                      mt: 4,
                      display: step !== 2 || isMobile ? "none" : undefined,
                      justifyContent: "space-between",
                      height: 77,
                      width: 271,
                      borderRadius: 50,
                      fontSize: 19,
                    }}
                    variant="contained"
                    color="primary"
                    path="/choose-level"
                    endIcon={<Arrow />}
                  >
                    Inizia
                  </AppButton>
                </Box>
                <Stack flex={1} spacing={3} justifyContent="center">
                  <Stack spacing={1} direction="row" alignItems="flex-start">
                    <Box
                      sx={{
                        width: {
                          xs: 60,
                          md: 90,
                        },
                        height: {
                          xs: 60,
                          md: 90,
                        },
                        backgroundColor: "#F7F7F7",
                        borderRadius: 30,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        marginRight: {
                          md: "10px",
                        },
                      }}
                    >
                      <Gif alt="" src={freezeGif} />
                    </Box>
                    <Box
                      sx={{
                        width: {
                          xs: "calc(100% - 70px)",
                          md: "calc(100% - 110px)",
                        },
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: {
                            xs: 18,
                            md: 30,
                          },
                          textTransform: "uppercase",
                          lineHeight: 1.2,
                        }}
                      >
                        <strong>FERMATEMPO</strong>
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: {
                            xs: 16,
                            md: 24,
                          },
                          fontWeight: 400,
                          lineHeight: "26px",
                          textTransform: "none",
                        }}
                      >
                        Ti dà 5 secondi in più per completare il puzzle.
                      </Typography>
                    </Box>
                  </Stack>
                  <Stack spacing={1} direction="row" alignItems="flex-start">
                    <Box
                      sx={{
                        width: {
                          xs: 60,
                          md: 90,
                        },
                        height: {
                          xs: 60,
                          md: 90,
                        },
                        backgroundColor: "#F7F7F7",
                        borderRadius: 30,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        marginRight: {
                          md: "10px",
                        },
                      }}
                    >
                      <Box sx={{ width: "50%" }}>
                        <Gif alt="" src={frecciaGif} />
                      </Box>
                    </Box>
                    <Box
                      sx={{
                        width: {
                          xs: "calc(100% - 70px)",
                          md: "calc(100% - 110px)",
                        },
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: {
                            xs: 18,
                            md: 30,
                          },
                          textTransform: "uppercase",
                          lineHeight: 1.2,
                        }}
                      >
                        <strong>FERMATESSERA</strong>
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: {
                            xs: 16,
                            md: 24,
                          },
                          fontWeight: 400,
                          lineHeight: "26px",
                          textTransform: "none",
                        }}
                      >
                        Ti mette una tessera nella posizione corretta, facendoti
                        risparmiare tempo.
                      </Typography>
                    </Box>
                  </Stack>
                </Stack>
                <Spacer />
              </Stack>
            </Slide>
          </SlideContainer>
        </Container>
        <SliderIcon
          sx={{
            right: 0,
            display: step < 2 ? undefined : "none",
          }}
          onClick={goToNextStep}
        >
          <RightArrow />
        </SliderIcon>
        <SliderIcon
          sx={{
            left: 0,
            transform: "rotate(180deg)",
            display: step > 0 ? undefined : "none",
          }}
          onClick={goToPrevStep}
        >
          <RightArrow />
        </SliderIcon>
      </Container>
      <Box
        sx={{
          position: "absolute",
          bottom: {
            xs: isTallEnough ? 80 : 20,
            md: 40,
          },
          width: "100%",
          height: "fit-content",
          left: 0,
          right: 0,
          display: "flex",
          alignItems: "center",
          flexDirection: "column",
        }}
      >
        {!(step !== 2 || !isMobile) && (
          <AppButton
            sx={{
              mb: 4,
              justifyContent: "space-between",
            }}
            variant="contained"
            color="primary"
            path="/choose-level"
            endIcon={<Arrow />}
          >
            Inizia
          </AppButton>
        )}
        <Stack spacing={2} direction="row">
          <Dot active={step === 0} onClick={() => goToStep(0)} />
          <Dot active={step === 1} onClick={() => goToStep(1)} />
          <Dot active={step === 2} onClick={() => goToStep(2)} />
        </Stack>
        <AppButton
          sx={{
            mt: 1,
            color: "#fff",
            minWidth: 0,
            opacity: step === 2 ? 0 : undefined,
            fontSize: {
              xs: 16,
              md: 28,
            },
            fontWeight: 400,
            fontFamily: "Futura PT",
          }}
          variant="text"
          path="/choose-level"
          disabled={step === 2}
        >
          Salta il tutorial
        </AppButton>
      </Box>
    </PageWrapper>
  );
});
